import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { Badge } from "../Badge";

const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-teal/60 focus:border-brand-teal";

export default function PatientProfile() {
  const [data, setData] = useState(undefined);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/patient/overview").then((r) => {
    setData(r.data);
    const p = r.data.patient;
    setForm({
      phone: p.phone || "", address: p.address || "",
      emergency_name: p.emergency_name || "", emergency_relationship: p.emergency_relationship || "",
      emergency_phone: p.emergency_phone || "", emergency_email: p.emergency_email || "",
    });
  }).catch(() => setData(false));

  useEffect(() => { load(); }, []);

  if (data === undefined) return <p className="text-slate-500 font-semibold">Loading your profile…</p>;
  if (data === false) return <p className="text-red-700 font-semibold">Unable to load your profile.</p>;

  const p = data.patient;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/patient/profile", form);
      toast.success("Your contact details have been updated.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="patient-profile" className="max-w-2xl">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">My Profile</h1>
      <p className="mt-2 text-[15px] text-slate-600">Your details on file with BMS Health Care Services.</p>

      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="font-display text-2xl text-brand-navy">{p.full_name}</h2>
          <Badge kind="patient" value={p.status} />
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-[15px]">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Date of Birth</p>
            <p className="mt-1 text-brand-navy font-medium">{p.dob || "—"}</p>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email (login)</p>
            <p className="mt-1 text-brand-navy font-medium break-all">{p.email}</p>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h2 className="font-display text-xl text-brand-navy">Update Contact Details</h2>
        <p className="mt-1 text-sm text-slate-500">Keep your phone, address, and emergency contact up to date.</p>
        <form onSubmit={save} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="pt-phone" className="block text-sm font-bold text-brand-navy">Phone Number</label>
            <input id="pt-phone" data-testid="pt-phone" type="tel" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label htmlFor="pt-address" className="block text-sm font-bold text-brand-navy">Home Address</label>
            <input id="pt-address" data-testid="pt-address" type="text" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label htmlFor="pt-em-name" className="block text-sm font-bold text-brand-navy">Emergency Contact Name</label>
            <input id="pt-em-name" data-testid="pt-em-name" type="text" value={form.emergency_name || ""} onChange={(e) => set("emergency_name", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label htmlFor="pt-em-rel" className="block text-sm font-bold text-brand-navy">Relationship</label>
            <input id="pt-em-rel" data-testid="pt-em-rel" type="text" value={form.emergency_relationship || ""} onChange={(e) => set("emergency_relationship", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label htmlFor="pt-em-phone" className="block text-sm font-bold text-brand-navy">Emergency Contact Phone</label>
            <input id="pt-em-phone" data-testid="pt-em-phone" type="tel" value={form.emergency_phone || ""} onChange={(e) => set("emergency_phone", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div>
            <label htmlFor="pt-em-email" className="block text-sm font-bold text-brand-navy">Emergency Contact Email</label>
            <input id="pt-em-email" data-testid="pt-em-email" type="email" value={form.emergency_email || ""} onChange={(e) => set("emergency_email", e.target.value)} className={`mt-2 ${inputCls}`} />
          </div>
          <div className="sm:col-span-2">
            <button type="submit" data-testid="pt-profile-save" disabled={saving} className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
